import { useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { rqKeys } from '@/react-query-keys'
import { useAppStore } from '@/store'
import { fetchVisitorMessageUnreadList } from './api'
import { useMessagesStore } from './store'

const toUserId = (value: unknown): number | null => {
  const id = Number(value)
  if (!Number.isFinite(id) || id <= 0) return null
  return id
}

export const useMessagesUnreadBootstrap = () => {
  const queryClient = useQueryClient()
  const isAuthorized = useAppStore((s) => s.isAuthorized)
  const userIdRaw = useAppStore((s) => s.userData.id)
  const setUnreadMessages = useMessagesStore((s) => s.setUnreadMessages)
  const isMessageMine = useMessagesStore((s) => s.isMessageMine)
  const resetMessages = useMessagesStore((s) => s.reset)

  const userId = useMemo(() => toUserId(userIdRaw), [ userIdRaw ])
  const previousUserIdRef = useRef<number | null>(null)

  useEffect(() => {
    const previousUserId = previousUserIdRef.current

    if (previousUserId !== userId) {
      resetMessages()
      queryClient.removeQueries({
        queryKey: [ rqKeys.visitorMessageUnread ],
        exact: false,
      })
    } else if (!isAuthorized || !userId) {
      resetMessages()
    }

    previousUserIdRef.current = userId
  }, [ isAuthorized, userId, resetMessages, queryClient ])

  const isEnabled = isAuthorized && !!userId

  const {
    data: unreadList,
  } = useQuery({
    queryKey: [ rqKeys.visitorMessageUnread, userId ],
    queryFn: fetchVisitorMessageUnreadList,
    enabled: isEnabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!isEnabled) return
    if (!unreadList) return

    setUnreadMessages(unreadList.filter((item) => !isMessageMine(userId, item)))
  }, [ unreadList, setUnreadMessages, isMessageMine, userId, isEnabled ])
}
